import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import { useSEO } from '@/hooks/useSEO';

interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

const categories = [
  { id: 'todos', label: 'Todas as Perguntas', icon: 'ri-questionnaire-line' },
  { id: 'cadeiras', label: 'Cadeiras Ergonômicas', icon: 'ri-armchair-line' },
  { id: 'mesas', label: 'Mesas Corporativas', icon: 'ri-table-line' },
  { id: 'divisorias', label: 'Divisórias e Cabines', icon: 'ri-layout-grid-line' },
  { id: 'marcas', label: 'Marcas e Garantia', icon: 'ri-award-line' },
  { id: 'entrega', label: 'Entrega e Montagem', icon: 'ri-truck-line' },
  { id: 'orcamento', label: 'Orçamento e Compra', icon: 'ri-file-list-3-line' },
  { id: 'turnkey', label: 'Projetos Turnkey', icon: 'ri-building-line' },
  { id: 'licitacoes', label: 'Licitações', icon: 'ri-government-line' },
];

const faqData: FaqItem[] = [
  // Cadeiras Ergonômicas
  {
    category: 'cadeiras',
    question: 'As cadeiras da Home Office Premium são certificadas pela NR-17?',
    answer: 'Sim. Todos os modelos de cadeiras ergonômicas Frisokar e Plaxmetal comercializados pela Home Office Premium atendem os requisitos da NR-17 (Norma Regulamentadora nº 17 do Ministério do Trabalho). A norma exige regulagem de altura do assento, suporte lombar ajustável, bordas arredondadas, tecido respirável e base com 5 rodas giratórias. Todos esses itens estão presentes nos modelos disponibilizados.',
  },
  {
    category: 'cadeiras',
    question: 'O que a NR-17 exige especificamente para cadeiras de trabalho?',
    answer: 'A NR-17 determina que cadeiras usadas em postos de trabalho com operações sentadas devem ter: altura do assento ajustável à estatura do trabalhador, encosto com suporte à região lombar, borda frontal do assento arredondada, superfície acolchoada e não escorregadia. Para trabalhos com computador, ainda é recomendado apoio para os braços e encosto reclinável. Nossa linha cobre todos esses requisitos.',
  },
  {
    category: 'cadeiras',
    question: 'Qual o impacto de cadeiras inadequadas no afastamento de funcionários?',
    answer: 'Cadeiras sem ergonomia são uma das principais causas de LER/DORT (Lesões por Esforços Repetitivos e Distúrbios Osteomusculares Relacionados ao Trabalho), que respondem por grande parte dos afastamentos pelo INSS no Brasil. Além do custo humano, a empresa pode sofrer ações trabalhistas e autuações da Fiscalização do Trabalho. Investir em cadeiras NR-17 é prevenção ativa e pode reduzir em até 40% o índice de afastamentos por dor postural.',
  },
  {
    category: 'cadeiras',
    question: 'Quais mecanismos de regulagem as cadeiras ergonômicas precisam ter?',
    answer: 'As cadeiras ergonômicas devem ter no mínimo: regulagem de altura do assento (por pistão a gás), regulagem do encosto lombar (altura e tensão), regulagem do apoio de braço (altura e largura nos modelos executivos), e mecanismo de inclinação do encosto (relax ou sincronizado). Os modelos Frisokar Diretor e Executivo possuem todos esses mecanismos. Os modelos operacionais atendem o mínimo exigido pela NR-17.',
  },
  {
    category: 'cadeiras',
    question: 'Qual é a garantia das cadeiras Frisokar e Plaxmetal?',
    answer: 'As cadeiras Frisokar possuem garantia de 5 anos contra defeitos de fabricação na estrutura e base, e 1 ano para componentes mecânicos como molas e pistão. A Plaxmetal oferece garantia de 2 anos na estrutura e 1 ano em componentes. A Home Office Premium presta assistência técnica local em Caruaru PE, sem precisar enviar a peça para outra cidade.',
  },
  {
    category: 'cadeiras',
    question: 'Qual é a diferença entre cadeira operacional, executiva e presidencial?',
    answer: 'Cadeira operacional: encosto baixo ou médio, sem apoio de braço ou com apoio fixo, indicada para call center, recepção e funcionários em geral. Cadeira executiva: encosto alto, apoio de braço regulável, geralmente em couro ou PU, para analistas e coordenadores. Cadeira presidencial: encosto ultra-alto (high back), espuma de alta densidade, couro genuíno ou ecológico premium, mecanismo relax — para direções e salas de reunião de alto padrão.',
  },
  {
    category: 'cadeiras',
    question: 'Como fazer um diagnóstico ergonômico antes de comprar cadeiras?',
    answer: 'A Home Office Premium oferece visita técnica gratuita em Caruaru PE para empresas interessadas em adequar o mobiliário à NR-17. Durante a visita, avaliamos os postos de trabalho, o perfil dos funcionários (altura média, tipo de tarefa, tempo sentado por dia) e indicamos os modelos mais adequados. Após a visita, entregamos um relatório com recomendações e orçamento detalhado.',
  },
  {
    category: 'cadeiras',
    question: 'Vocês atendem empresas com grande volume de cadeiras?',
    answer: 'Sim! Temos condições especiais para pedidos acima de 10 unidades, com desconto progressivo, prazo diferenciado e entrega + montagem inclusa. Atendemos empresas em Caruaru, Bezerros, Gravatá, Santa Cruz do Capibaribe, Garanhuns, Arcoverde e toda a região do Agreste e Sertão de Pernambuco.',
  },

  // Mesas Corporativas
  {
    category: 'mesas',
    question: 'Quais tipos de mesas corporativas a Home Office Premium vende?',
    answer: 'Trabalhamos com mesas operacionais, executivas, de reunião, em L, em pé (standing desk), para call center, estação de trabalho e mesas de apoio. As marcas principais são Marzo Vitorino e Pandim, com opções em MDP, MDF, vidro e aço. Também fazemos projetos sob medida para escritórios de todos os portes.',
  },
  {
    category: 'mesas',
    question: 'As mesas Marzo Vitorino são certificadas pelo FSC?',
    answer: 'Sim. A Marzo Vitorino possui certificação FSC (Forest Stewardship Council), que garante que a madeira utilizada vem de florestas manejadas de forma responsável e sustentável. Isso é um diferencial importante para empresas que buscam certificações ambientais como ISO 14001 ou que precisam atender requisitos de sustentabilidade em licitações.',
  },
  {
    category: 'mesas',
    question: 'Vocês fazem mesas sob medida para escritórios?',
    answer: 'Sim. Além dos modelos de catálogo, oferecemos mesas sob medida com dimensões, acabamentos e configurações personalizadas. Trabalhamos com projetos 3D gratuitos para que o cliente visualize o resultado final antes da compra. O prazo de produção para mesas sob medida varia de 15 a 30 dias úteis.',
  },
  {
    category: 'mesas',
    question: 'Qual é a garantia das mesas corporativas?',
    answer: 'As mesas Marzo Vitorino possuem garantia de 3 anos na estrutura e 1 ano em ferragens. As mesas Pandim têm garantia de 2 anos. A garantia cobre defeitos de fabricação e problemas estruturais. Nossa equipe técnica realiza manutenção e reparos em Caruaru e região, sem necessidade de envio para outras cidades.',
  },
  {
    category: 'mesas',
    question: 'As mesas vêm com passa-fios e organização de cabos?',
    answer: 'Sim. A maioria dos modelos de mesas operacionais e executivas já inclui passa-fios, canaletas ou caixas de tomadas integradas. Para projetos maiores, oferecemos soluções completas de gestão de cabos com eletrodutos, calhas e tomadas sob a mesa. Isso é essencial para manter o escritório organizado e seguro.',
  },

  // Divisórias e Cabines
  {
    category: 'divisorias',
    question: 'As divisórias modulares precisam de obra para instalação?',
    answer: 'Não. As divisórias modulares da Home Office Premium são instaladas sem necessidade de quebra de paredes, reboco ou pintura. O sistema é de encaixe e parafusamento direto no piso e no teto, com acabamento impecável. A instalação é rápida — um escritório de 200m² pode ser dividido em 1 a 2 dias — e não gera poeira ou entulho.',
  },
  {
    category: 'divisorias',
    question: 'Qual a altura máxima das divisórias modulares?',
    answer: 'As divisórias modulares podem atingir até 3 metros de altura, dependendo do modelo. Oferecemos divisórias de meia-altura (1,20m), altura padrão (2,40m) e altura total (até o teto). Também temos opções com vidro, persiana integrada, porta de correr e painel acústico. Todas as configurações são personalizáveis.',
  },
  {
    category: 'divisorias',
    question: 'As cabines acústicas realmente isolam o som?',
    answer: 'Sim. Nossas cabines acústicas possuem isolamento de até 45dB, o que significa que conversas normais do lado de fora não são ouvidas dentro da cabine, e vice-versa. A construção utiliza painéis de absorção acústica, vedação de portas e sistema de ventilação silencioso. São ideais para reuniões, ligações, gravações e concentração.',
  },
  {
    category: 'divisorias',
    question: 'As divisórias podem ser realocadas para outro escritório?',
    answer: 'Sim. Uma das grandes vantagens das divisórias modulares é a relocabilidade. Os painéis podem ser desmontados, transportados e remontados em outro ambiente sem perda de qualidade. Isso é ideal para empresas que mudam de endereço ou que precisam reconfigurar o layout do escritório periodicamente.',
  },
  {
    category: 'divisorias',
    question: 'Vocês vendem divisórias sanitárias (para banheiros)?',
    answer: 'Sim. Trabalhamos com divisórias sanitárias da marca Pertech, em laminado melamínico ou fenólico, resistentes à umidade e ao impacto. São indicadas para escritórios, escolas, shoppings, clínicas e indústrias. Oferecemos instalação completa com garantia.',
  },

  // Marcas e Garantia
  {
    category: 'marcas',
    question: 'Quais marcas de móveis corporativos a Home Office Premium vende?',
    answer: 'Trabalhamos com as principais marcas do mercado: Frisokar e Plaxmetal (cadeiras ergonômicas NR-17), Marzo Vitorino e Pandim (mesas corporativas), W3 Indústria (mobiliários de aço), Rivatti (móveis para restaurante e food service) e Pertech (divisórias sanitárias). Todas são marcas consolidadas com certificações e garantia.',
  },
  {
    category: 'marcas',
    question: 'A Home Office Premium é revendedor autorizado Frisokar?',
    answer: 'Sim. Somos revendedores autorizados Frisokar em Caruaru PE, com acesso a todo o catálogo da marca, preços oficiais e garantia integral. Isso significa que você compra com nota fiscal, garantia de fábrica e assistência técnica local — sem intermediários.',
  },
  {
    category: 'marcas',
    question: 'A Home Office Premium é revendedor autorizado Plaxmetal?',
    answer: 'Sim. Somos revendedores autorizados Plaxmetal em Caruaru PE. A Plaxmetal é uma das maiores fabricantes de cadeiras ergonômicas do Brasil, com linha completa de operacionais, executivas e presidenciais. Comprando conosco, você tem garantia de fábrica, nota fiscal e suporte técnico local.',
  },
  {
    category: 'marcas',
    question: 'O que é a certificação FSC da Marzo Vitorino?',
    answer: 'A certificação FSC (Forest Stewardship Council) garante que a madeira utilizada nos móveis Marzo Vitorino vem de florestas manejadas de forma responsável, com respeito ao meio ambiente, aos trabalhadores e às comunidades locais. É um selo reconhecido internacionalmente e valorizado em licitações e certificações empresariais.',
  },
  {
    category: 'marcas',
    question: 'A W3 Indústria atende normas NR-18 e NR-24?',
    answer: 'Sim. Os mobiliários de aço da W3 Indústria atendem as normas NR-18 (Condições e Meio Ambiente de Trabalho na Indústria da Construção) e NR-24 (Condições Sanitárias e de Conforto nos Locais de Trabalho). Isso inclui armários, vestiários, cozinhas industriais, beliches e móveis para alojamentos — todos com resistência estrutural e acabamento industrial.',
  },

  // Entrega e Montagem
  {
    category: 'entrega',
    question: 'A entrega e montagem estão inclusas na compra?',
    answer: 'Sim. Para compras na região de Caruaru e Agreste Pernambucano, a entrega e montagem são gratuitas. Para outras regiões de Pernambuco (Recife, Petrolina, Garanhuns, etc.), cobramos um valor simbólico de frete que varia conforme a distância e o volume. Sempre informamos o valor do frete no orçamento, sem surpresas.',
  },
  {
    category: 'entrega',
    question: 'Qual é o prazo de entrega dos móveis corporativos?',
    answer: 'O prazo varia conforme o produto: cadeiras e itens de pronta-entrega são entregues em 3 a 7 dias úteis; mesas e itens sob medida levam 15 a 30 dias úteis; projetos Turnkey completos podem levar 30 a 60 dias. Sempre informamos o prazo exato no orçamento e acompanhamos a produção para garantir a entrega no prazo combinado.',
  },
  {
    category: 'entrega',
    question: 'Vocês entregam em todo o Nordeste?',
    answer: 'Sim. Atendemos todo o estado de Pernambuco e expandimos para Alagoas, Paraíba, Rio Grande do Norte, Ceará, Bahia e Maranhão. Para estados mais distantes, o frete é calculado individualmente. Também atendemos empresas com filiais em múltiplas cidades com logística centralizada.',
  },
  {
    category: 'entrega',
    question: 'A montagem é feita por equipe especializada?',
    answer: 'Sim. Nossa equipe de montagem é treinada e especializada em mobiliário corporativo. Eles sabem montar cadeiras ergonômicas com regulagem correta, divisórias modulares com alinhamento perfeito, mesas com passa-fios organizados e cabines acústicas com vedação adequada. A montagem inclui ajustes finais e orientação ao cliente.',
  },
  {
    category: 'entrega',
    question: 'Vocês fazem remanejamento de móveis no escritório?',
    answer: 'Sim. Oferecemos serviço de remanejamento para empresas que precisam reorganizar o escritório sem comprar móveis novos. Nossa equipe desmonta, transporta e remonta os móveis no novo layout, com ajustes e manutenção preventiva inclusos. É uma solução econômica para empresas em crescimento.',
  },

  // Orçamento e Compra
  {
    category: 'orcamento',
    question: 'Como solicitar um orçamento de móveis corporativos?',
    answer: 'Você pode solicitar um orçamento de três formas: pelo WhatsApp (81) 98203-0285, pelo formulário de contato no site ou agendando uma visita ao nosso showroom em Caruaru com hora marcada. Para orçamentos mais precisos, recomendamos enviar as medidas do ambiente, fotos e a quantidade aproximada de funcionários. Respondemos em até 24 horas úteis.',
  },
  {
    category: 'orcamento',
    question: 'O orçamento é gratuito?',
    answer: 'Sim. Todos os orçamentos são gratuitos e sem compromisso. Para projetos maiores, oferecemos visita técnica gratuita em Caruaru e região, medição do ambiente, projeto 3D e relatório de adequação à NR-17. Tudo sem custo para o cliente.',
  },
  {
    category: 'orcamento',
    question: 'Vocês aceitam cartão de crédito e parcelamento?',
    answer: 'Sim. Aceitamos cartão de crédito em até 12x (com acréscimo da operadora), boleto bancário, PIX e transferência. Para empresas, também trabalhamos com faturamento em 30/60 dias mediante aprovação cadastral. Para licitações, seguimos as condições de pagamento editalícias.',
  },
  {
    category: 'orcamento',
    question: 'É possível negociar desconto para compras em grande volume?',
    answer: 'Sim. Oferecemos desconto progressivo conforme o volume: 5% para 10 a 20 unidades, 8% para 21 a 50 unidades, 12% para 51 a 100 unidades e 15% para acima de 100 unidades. O desconto pode ser combinado com condições de pagamento diferenciadas. Entre em contato para uma proposta personalizada.',
  },
  {
    category: 'orcamento',
    question: 'Vocês vendem para pessoa física ou só para empresas?',
    answer: 'Atendemos tanto empresas (B2B) quanto pessoas físicas. Para pessoas físicas, oferecemos cadeiras home office, mesas para home office, estantes e organizadores. O atendimento é o mesmo: qualidade, garantia e entrega + montagem. A diferença é que empresas têm acesso a condições especiais de volume e faturamento.',
  },

  // Projetos Turnkey
  {
    category: 'turnkey',
    question: 'O que é um projeto Turnkey de escritório?',
    answer: 'Projeto Turnkey ("chave na mão") é quando a Home Office Premium assume todo o processo de implantação do escritório: projeto arquitetônico, fornecimento de mobiliário, instalação elétrica e de rede, divisórias, cabines acústicas, iluminação, ar-condicionado, pintura e acabamentos. O cliente recebe o escritório pronto para usar.',
  },
  {
    category: 'turnkey',
    question: 'Qual é o prazo de um projeto Turnkey completo?',
    answer: 'O prazo varia conforme o tamanho e a complexidade do projeto. Um escritório de 100m² leva em média 30 a 45 dias; um de 500m² leva 60 a 90 dias. O prazo inclui projeto, aprovação, produção dos móveis, execução da obra e entrega final. Trabalhamos com cronograma detalhado e acompanhamento semanal.',
  },
  {
    category: 'turnkey',
    question: 'O projeto 3D é gratuito?',
    answer: 'Sim. O projeto 3D é gratuito para empresas que fecham o projeto conosco. Mesmo para orçamentos iniciais, oferecemos uma pré-visualização 3D básica para que o cliente entenda o potencial do espaço. O projeto 3D detalhado inclui layout, mobiliário, iluminação, cores e texturas, com renderizações realistas.',
  },
  {
    category: 'turnkey',
    question: 'Vocês fazem projetos para clínicas, escolas e restaurantes?',
    answer: 'Sim. Além de escritórios corporativos, atuamos em projetos para clínicas médicas e odontológicas, escolas e universidades, restaurantes e lanchonetes, igrejas e templos, indústrias e alojamentos. Cada segmento tem necessidades específicas e nossa equipe está preparada para atender com mobiliário adequado à norma de cada setor.',
  },
  {
    category: 'turnkey',
    question: 'O que está incluso no pós-venda de um projeto Turnkey?',
    answer: 'O pós-venda inclui: garantia dos móveis (conforme marca), visita de acompanhamento após 30 dias, manutenção preventiva anual, suporte técnico por telefone e WhatsApp, e prioridade em novos pedidos. Para contratos de manutenção anual, oferecemos desconto de 10% em todos os serviços.',
  },

  // Licitações
  {
    category: 'licitacoes',
    question: 'A Home Office Premium atende licitações públicas?',
    answer: 'Sim. Atendemos licitações públicas e privadas para mobiliário corporativo e escolar em Caruaru e todo o Nordeste. Temos experiência em pregões eletrônicos, concorrências, tomada de preços e convites. Oferecemos documentação completa, nota fiscal, certidões e suporte técnico especializado durante todo o processo licitatório.',
  },
  {
    category: 'licitacoes',
    question: 'Quais documentos são necessários para participar de uma licitação?',
    answer: 'Os documentos básicos incluem: certidão negativa de débitos (CND), certidão negativa de falência e concordata, certidão regularidade do FGTS, certidão regularidade do INSS, certidão de regularidade fiscal estadual e municipal, atestado de capacidade técnica, e certificado de origem dos produtos (quando exigido). Auxiliamos o cliente na preparação de toda a documentação.',
  },
  {
    category: 'licitacoes',
    question: 'Vocês já atenderam licitações para órgãos públicos?',
    answer: 'Sim. Já atendemos licitações para prefeituras, câmaras municipais, secretarias de educação, universidades, hospitais públicos, tribunais e forças armadas em Pernambuco e estados vizinhos. Nosso portfólio inclui projetos de mobiliário escolar, corporativo e hospitalar com entrega e montagem em todo o Nordeste.',
  },
  {
    category: 'licitacoes',
    question: 'Os produtos para licitação têm certificação de origem?',
    answer: 'Sim. Todos os produtos comercializados pela Home Office Premium possuem certificado de origem, nota fiscal e garantia de fábrica. Para licitações que exigem certificações específicas (como FSC da Marzo Vitorino ou certificação NR-17 das cadeiras), fornecemos a documentação completa junto com a proposta.',
  },

  // Gerais
  {
    category: 'orcamento',
    question: 'Onde fica a loja Home Office Premium em Caruaru?',
    answer: 'A Home Office Premium está localizada na Av. Agamenon Magalhães, 1519, bairro Maurício de Nassau, Caruaru - PE, CEP 55014-000. Nosso horário comercial é de segunda a sexta das 8h às 18h e sábado das 9h às 13h. Já o showroom funciona com hora marcada — você agenda pelo WhatsApp, escolhe o melhor dia e horário (inclusive sábado à noite ou domingo), e recebe atendimento exclusivo com café e bolo, sem pressa e sem interrupção.',
  },
  {
    category: 'orcamento',
    question: 'Como funciona a garantia da Home Office Premium?',
    answer: 'A garantia varia conforme o produto e a marca: cadeiras Frisokar têm 5 anos de garantia, cadeiras Plaxmetal 2 anos, mesas Marzo Vitorino 3 anos, mesas Pandim 2 anos, divisórias 2 anos e cabines acústicas 2 anos. A garantia cobre defeitos de fabricação e problemas estruturais. Assistência técnica é prestada localmente em Caruaru PE.',
  },
  {
    category: 'orcamento',
    question: 'Vocês oferecem manutenção preventiva de mobiliário corporativo?',
    answer: 'Sim. Oferecemos contratos de manutenção preventiva para empresas com grande quantidade de mobiliário. A manutenção inclui: ajuste de cadeiras, lubrificação de mecanismos, reaperto de parafusos, troca de rodízios, reparo de divisórias e organização de cabos. O contrato anual inclui 4 visitas e desconto de 10% em peças de reposição.',
  },
  {
    category: 'orcamento',
    question: 'A Home Office Premium vende móveis escolares?',
    answer: 'Sim. Temos uma linha completa de mobiliário escolar: carteiras escolares, cadeiras universitárias, mesas para laboratório, armários para professores, estantes para biblioteca, mesas para refeitório e móveis para sala de informática. Atendemos escolas públicas, particulares, universidades e creches em todo o Nordeste.',
  },
  {
    category: 'orcamento',
    question: 'Vocês vendem móveis para restaurantes e lanchonetes?',
    answer: 'Sim. Trabalhamos com a marca Rivatti, especializada em móveis para restaurante, lanchonete, padaria, food truck e food service. A linha inclui mesas, cadeiras, banquetas, sofás, balcões e móveis sob medida para área gourmet. Todos em aço inox, madeira ou combinações, com acabamento resistente à umidade e ao uso intenso.',
  },
];

function FaqAccordion({ question, answer, isOpen, onToggle }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-background-200/70">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 text-left cursor-pointer group"
      >
        <span className={`text-[15px] md:text-base font-medium pr-6 leading-snug transition-colors ${isOpen ? 'text-primary-600' : 'text-foreground-800 group-hover:text-primary-600'}`}>
          {question}
        </span>
        <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-primary-600 text-white rotate-180' : 'bg-background-100 text-foreground-500'}`}>
          <i className="ri-arrow-down-s-line text-base"></i>
        </div>
      </button>
      {isOpen && (
        <div className="pb-6">
          <p className="text-[14px] text-foreground-500 leading-relaxed font-light max-w-2xl">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState('todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useSEO({
    title: 'Perguntas Frequentes sobre Móveis Corporativos | Home Office Premium',
    description: 'Tire todas as suas dúvidas sobre cadeiras ergonômicas NR-17, mesas corporativas, divisórias modulares, garantia, entrega, orçamento e projetos Turnkey em Caruaru PE e todo o Nordeste.',
    keywords: 'FAQ móveis corporativos, dúvidas cadeiras ergonômicas, perguntas divisórias modulares, garantia mobiliário escritório, orçamento móveis Caruaru, entrega montagem Pernambuco',
    path: '/faq',
    ogTitle: 'Perguntas Frequentes sobre Móveis Corporativos | Home Office Premium',
    ogDescription: 'Respostas para as principais dúvidas sobre cadeiras NR-17, mesas, divisórias, garantia, entrega e projetos Turnkey em Caruaru PE.',
  });

  const filteredFaqs = useMemo(() => {
    let result = faqData;
    if (activeCategory !== 'todos') {
      result = result.filter(f => f.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q));
    }
    return result;
  }, [activeCategory, searchQuery]);

  const faqCountByCategory = useMemo(() => {
    const counts: Record<string, number> = { todos: faqData.length };
    faqData.forEach(f => {
      counts[f.category] = (counts[f.category] || 0) + 1;
    });
    return counts;
  }, []);

  useEffect(() => {
    const schemaId = 'schema-faq';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqData.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });

    const breadcrumbId = 'schema-faq-breadcrumb';
    let breadcrumbScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.id = breadcrumbId;
      breadcrumbScript.type = 'application/ld+json';
      document.head.appendChild(breadcrumbScript);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Perguntas Frequentes', item: `${siteUrl}/faq` },
      ],
    });

    return () => {
      script?.remove();
      breadcrumbScript?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppFloat />
      <div className="pt-36 md:pt-44">
        {/* Hero */}
        <div className="pb-12 md:pb-16">
          <div className="max-w-[1200px] mx-auto px-5 md:px-8">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-400 mb-8">
              <Link to="/" className="hover:text-foreground-700 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 font-medium">Perguntas Frequentes</span>
            </nav>
            <div className="max-w-2xl">
              <span className="text-primary-600 text-[11px] font-semibold tracking-[0.2em] uppercase">Central de Ajuda</span>
              <h1 className="mt-4 text-[36px] md:text-[52px] font-semibold text-foreground-900 leading-[1.05] tracking-tight">
                Perguntas Frequentes
              </h1>
              <p className="mt-5 text-[15px] text-foreground-500 leading-relaxed font-light">
                Encontre respostas para as principais dúvidas sobre cadeiras ergonômicas NR-17, mesas corporativas, divisórias modulares, garantia, entrega, orçamento e projetos Turnkey. São {faqData.length} perguntas organizadas por categoria.
              </p>
            </div>
          </div>
        </div>

        {/* Search + Categories */}
        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Search */}
          <div className="max-w-xl mx-auto mb-10">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-foreground-400"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setOpenIndex(null); }}
                placeholder="Buscar perguntas..."
                className="w-full pl-12 pr-4 py-3.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-500 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setOpenIndex(null); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            {searchQuery && (
              <p className="text-xs text-foreground-400 mt-2 text-center">
                {filteredFaqs.length} resultado{filteredFaqs.length !== 1 ? 's' : ''} encontrado{filteredFaqs.length !== 1 ? 's' : ''} para "{searchQuery}"
              </p>
            )}
          </div>

          {/* Category tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setOpenIndex(null); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.id
                    ? 'bg-foreground-900 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className={`w-4 h-4 flex items-center justify-center ${activeCategory === cat.id ? 'text-white' : 'text-foreground-500'}`}>
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                  {faqCountByCategory[cat.id] ?? 0}
                </span>
              </button>
            ))}
          </div>

          {/* FAQ List */}
          <div className="max-w-3xl mx-auto divide-y divide-background-200/70 border-y border-background-200/70">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-4">
                  <i className="ri-question-mark text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">Nenhuma pergunta encontrada para "{searchQuery}"</p>
                <button
                  onClick={() => { setSearchQuery(''); setActiveCategory('todos'); }}
                  className="text-primary-600 text-sm font-medium mt-2 hover:underline cursor-pointer"
                >
                  Limpar busca
                </button>
              </div>
            ) : (
              filteredFaqs.map((faq, index) => (
                <FaqAccordion
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                />
              ))
            )}
          </div>

          {/* CTA */}
          <div className="max-w-3xl mx-auto mt-16">
            <div className="bg-foreground-950 rounded-2xl p-8 md:p-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-600/20 rounded-xl mx-auto mb-5">
                <i className="ri-message-3-line text-primary-500 text-xl"></i>
              </div>
              <h3 className="text-white text-[22px] md:text-[28px] font-semibold mb-3">Não encontrou sua dúvida?</h3>
              <p className="text-white/50 text-sm mb-7 max-w-md mx-auto font-light">
                Nosso time está pronto para responder qualquer pergunta sobre móveis corporativos, cadeiras NR-17, projetos Turnkey e orçamentos.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Tive%20uma%20d%C3%BAvida%20no%20FAQ%20e%20gostaria%20de%20ajuda"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="inline-flex items-center gap-2 bg-primary-600 text-white text-sm font-medium px-6 py-3.5 rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Falar no WhatsApp
                </a>
                <Link
                  to="/contato"
                  className="inline-flex items-center gap-2 border border-white/20 text-white text-sm font-medium px-6 py-3.5 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  Enviar mensagem
                </Link>
              </div>
            </div>
          </div>

          {/* Links internos */}
          <div className="max-w-3xl mx-auto mt-16 pt-12 border-t border-gray-100">
            <h2 className="text-[22px] font-semibold text-foreground-900 mb-8 text-center">Explore nossos produtos e serviços</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                { label: 'Cadeiras', path: '/cadeiras', icon: 'ri-armchair-line' },
                { label: 'Mesas', path: '/mesas', icon: 'ri-table-line' },
                { label: 'Divisórias', path: '/divisorias', icon: 'ri-layout-grid-line' },
                { label: 'Cabines', path: '/cabines-acusticas', icon: 'ri-sound-module-line' },
                { label: 'Aço W3', path: '/aco', icon: 'ri-archive-drawer-line' },
                { label: 'Turnkey', path: '/turnkey', icon: 'ri-building-line' },
                { label: 'Marcas', path: '/marcas', icon: 'ri-award-line' },
                { label: 'Contato', path: '/contato', icon: 'ri-message-3-line' },
              ].map(link => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center gap-2.5 p-4 bg-white rounded-xl hover:bg-background-50 transition-all cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                    <i className={`${link.icon} text-primary-600 text-sm`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}